import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  aiAssistantAPI, projectsAPI, tasksAPI, usersAPI, dashboardAPI,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Send, RefreshCw, Trash2, Sparkles, TrendingUp, AlertTriangle,
  CheckCircle2, Clock, Zap, Users, FolderKanban, Target, ChevronRight,
  ArrowUpRight, Brain, MessageSquare, Lightbulb, Shield, Search, X,
  Loader2, BarChart3, Activity, CircleDot, Flame, Star, ArrowRight,
  Cpu, Globe, Hash, Layers, GitBranch, Bell, Eye, Calendar,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Cell, RadialBarChart, RadialBar, PieChart, Pie, Legend,
} from 'recharts';

/* ─── Global CSS injected once ─── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

@keyframes ai-pulse { 0%,100%{opacity:.6;transform:scale(1)} 50%{opacity:1;transform:scale(1.05)} }
@keyframes ai-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
@keyframes ai-spin { to{transform:rotate(360deg)} }
@keyframes ai-bounce { 0%,80%,100%{transform:scale(.75);opacity:.5} 40%{transform:scale(1.1);opacity:1} }
@keyframes ai-fade-up { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:none} }
@keyframes ai-glow { 0%,100%{box-shadow:0 0 20px rgba(139,92,246,.2)} 50%{box-shadow:0 0 40px rgba(139,92,246,.5),0 0 80px rgba(6,214,160,.15)} }
@keyframes ai-slide-in { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:none} }
@keyframes ai-gradient { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
@keyframes ai-shimmer { from{background-position:-200% 0} to{background-position:200% 0} }
@keyframes ai-ring { 0%{transform:scale(.95);opacity:.8} 50%{transform:scale(1.05);opacity:.4} 100%{transform:scale(.95);opacity:.8} }
@keyframes ai-ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }

.ai-page * { box-sizing: border-box; }
.ai-page { font-family: 'Inter', var(--font-body), sans-serif; }

.ai-glass {
  background: rgba(var(--bg-card-rgb, 30,30,40), 0.6);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.07);
}

.ai-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 20px;
  transition: transform .22s cubic-bezier(.34,1.56,.64,1), box-shadow .22s ease;
}
.ai-card:hover { transform: translateY(-2px); }

.ai-metric-card {
  position: relative;
  overflow: hidden;
  cursor: default;
}
.ai-metric-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: inherit;
  border-radius: inherit;
  opacity: 0;
  transition: opacity .3s;
}
.ai-metric-card:hover::before { opacity: 1; }

.ai-send-btn:not(:disabled):hover { transform: scale(1.08) rotate(-2deg); }
.ai-send-btn:not(:disabled):active { transform: scale(.96); }
.ai-send-btn { transition: all .18s cubic-bezier(.34,1.56,.64,1); }

.ai-chat-input { transition: border-color .2s, box-shadow .2s; }
.ai-chat-input:focus { outline: none; border-color: #8b5cf6 !important; box-shadow: 0 0 0 3px rgba(139,92,246,.18) !important; }

.ai-suggestion-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(0,0,0,.2);
}
.ai-suggestion-chip { transition: all .18s ease; }

.ai-insight-row:hover { background: rgba(255,255,255,.035) !important; transform: translateX(3px); }
.ai-insight-row { transition: all .18s ease; cursor: pointer; }

.ai-skeleton {
  background: linear-gradient(90deg, var(--bg-hover) 25%, rgba(255,255,255,.06) 50%, var(--bg-hover) 75%);
  background-size: 200% 100%;
  animation: ai-shimmer 1.6s ease-in-out infinite;
  border-radius: 8px;
}

.ai-header-grad {
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 40%, #06d6a0 100%);
  background-size: 200% 200%;
  animation: ai-gradient 5s ease infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.ai-status-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #06d6a0;
  box-shadow: 0 0 0 0 rgba(6,214,160,.6);
  animation: ai-ring 2s ease infinite;
}

.ai-ticker-wrap { overflow: hidden; mask-image: linear-gradient(90deg,transparent 0%,black 8%,black 92%,transparent 100%); }
.ai-ticker-track { display: flex; width: max-content; animation: ai-ticker 28s linear infinite; }

.ai-msg-enter { animation: ai-fade-up .3s ease both; }
.ai-panel-in { animation: ai-fade-up .4s ease both; }
`;

/* ─── Palette ─── */
const ACCENT_COLORS = ['#8b5cf6','#6366f1','#06d6a0','#fbbf24','#f97316','#ef4444','#38bdf8','#a855f7'];
const STATUS_COLOR  = { Backlog:'#6366f1','To Do':'#8b5cf6','In Progress':'#a855f7','In Review':'#c084fc',Done:'#06d6a0' };
const PRIORITY_COLOR = { Low:'#38bdf8', Medium:'#fbbf24', High:'#f97316', Critical:'#ef4444' };

/* ─── Custom recharts tooltip ─── */
const RTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background:'var(--bg-card)', border:'1px solid rgba(255,255,255,.12)',
      borderRadius:12, padding:'10px 16px',
      boxShadow:'0 16px 40px rgba(0,0,0,.4)', minWidth:110,
    }}>
      <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',marginBottom:4,textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</div>
      {payload.map((p,i)=>(
        <div key={i} style={{fontSize:20,fontWeight:800,color:p.fill||p.color||'var(--text)',fontFamily:'Inter,sans-serif'}}>{p.value}</div>
      ))}
    </div>
  );
};

/* ─── Animated number ─── */
function AnimatedNum({ target, prefix='', suffix='' }) {
  const [val, setVal] = useState(0);
  useEffect(()=>{
    let start=0, end=Number(target||0), dur=900;
    if(start===end){ setVal(end); return; }
    const step=Math.ceil((end-start)/(dur/16));
    const t=setInterval(()=>{
      start=Math.min(start+step,end);
      setVal(start);
      if(start>=end) clearInterval(t);
    },16);
    return ()=>clearInterval(t);
  },[target]);
  return <>{prefix}{val}{suffix}</>;
}

/* ─── Metric Card ─── */
function MetricCard({ icon:Icon, label, value, sub, accent, trend, delay=0 }){
  const [hov,setHov]=useState(false);
  return (
    <div
      className="ai-card ai-metric-card ai-panel-in"
      style={{ padding:'22px 24px', animationDelay:`${delay}ms`,
        background: hov ? `linear-gradient(135deg, ${accent}14, ${accent}06)` : 'var(--bg-card)',
        borderColor: hov ? `${accent}40` : 'var(--border)',
      }}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
    >
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:14}}>
        <div style={{
          width:44, height:44, borderRadius:14, display:'grid',placeItems:'center',
          background:`linear-gradient(135deg,${accent}28,${accent}10)`,
          border:`1px solid ${accent}30`, flexShrink:0,
          animation: hov ? 'ai-float 2s ease infinite' : 'none',
        }}>
          <Icon size={20} color={accent} />
        </div>
        {trend !== undefined && (
          <div style={{
            display:'flex',alignItems:'center',gap:4,fontSize:11,fontWeight:700,
            color: trend>=0 ? '#06d6a0' : '#ef4444',
            background: trend>=0 ? 'rgba(6,214,160,.12)' : 'rgba(239,68,68,.12)',
            padding:'3px 8px',borderRadius:99,
          }}>
            <TrendingUp size={11} style={{transform: trend<0?'scaleY(-1)':'none'}}/>
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{label}</div>
      <div style={{fontSize:30,fontWeight:900,color:'var(--text)',lineHeight:1,fontFamily:'Inter,sans-serif',letterSpacing:'-0.03em'}}>
        <AnimatedNum target={value} />
      </div>
      {sub && <div style={{fontSize:11.5,color:accent,fontWeight:600,marginTop:5,display:'flex',alignItems:'center',gap:4}}>
        <CircleDot size={10}/>{sub}
      </div>}
      {/* glow bar */}
      <div style={{position:'absolute',bottom:0,left:0,right:0,height:2,borderRadius:'0 0 20px 20px',background:`linear-gradient(90deg,transparent,${accent},transparent)`,opacity:hov?1:0,transition:'opacity .3s'}}/>
    </div>
  );
}

/* ─── Message Bubble ─── */
function MsgBubble({ msg }){
  const isUser = msg.sender==='user';
  return (
    <div className="ai-msg-enter" style={{display:'flex',flexDirection:'column',alignItems:isUser?'flex-end':'flex-start',gap:4}}>
      {!isUser && (
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <div style={{width:20,height:20,borderRadius:'50%',background:'linear-gradient(135deg,#8b5cf6,#06d6a0)',display:'grid',placeItems:'center'}}>
            <Bot size={11} color="#fff"/>
          </div>
          <span style={{fontSize:10.5,color:'var(--text-muted)',fontWeight:600,letterSpacing:'.04em'}}>AI ASSISTANT</span>
        </div>
      )}
      <div style={{
        maxWidth:'84%', padding:'11px 16px',
        background: isUser
          ? 'linear-gradient(135deg,#8b5cf6,#7c3aed)'
          : 'rgba(255,255,255,.05)',
        backdropFilter: isUser ? 'none' : 'blur(10px)',
        color: isUser?'#fff':'var(--text)',
        borderRadius: isUser?'18px 18px 4px 18px':'4px 18px 18px 18px',
        fontSize:13.5, lineHeight:1.55,
        border: isUser?'none':'1px solid rgba(255,255,255,.1)',
        whiteSpace:'pre-wrap', wordBreak:'break-word',
        boxShadow: isUser?'0 6px 20px rgba(139,92,246,.3)':'none',
      }}>
        {msg.text}
      </div>
      <div style={{fontSize:9.5,color:'var(--text-muted)',opacity:.6}}>{msg.time}</div>
    </div>
  );
}

/* ─── Suggestion Chip ─── */
function SuggChip({ icon:Icon, label, prompt, accent, onSend }){
  return (
    <button
      className="ai-suggestion-chip"
      onClick={()=>onSend(prompt)}
      style={{
        display:'flex',alignItems:'center',gap:9,
        padding:'10px 14px', width:'100%', textAlign:'left',
        background:`linear-gradient(135deg,${accent}10,${accent}05)`,
        border:`1px solid ${accent}25`, borderRadius:12,
        cursor:'pointer',
      }}
    >
      <div style={{width:30,height:30,borderRadius:9,background:`${accent}20`,display:'grid',placeItems:'center',flexShrink:0}}>
        <Icon size={14} color={accent}/>
      </div>
      <div style={{flex:1}}>
        <div style={{fontSize:12,fontWeight:700,color:'var(--text)',marginBottom:1}}>{label}</div>
        <div style={{fontSize:10.5,color:'var(--text-muted)',lineHeight:1.3}}>{prompt}</div>
      </div>
      <ArrowRight size={12} color={accent} style={{flexShrink:0,opacity:.7}}/>
    </button>
  );
}

/* ─── Typing dots ─── */
const TypingDots = () => (
  <div style={{display:'flex',gap:5,padding:'2px 4px',alignItems:'center'}}>
    {[0,1,2].map(i=>(
      <span key={i} style={{
        width:7,height:7,borderRadius:'50%',background:'#8b5cf6',display:'inline-block',
        animation:`ai-bounce 1.2s ${i*.18}s infinite ease-in-out`,
      }}/>
    ))}
  </div>
);

/* ─── Section Header ─── */
const SH = ({icon:Icon, title, color='#8b5cf6', action, onAction}) => (
  <div style={{display:'flex',alignItems:'center',gap:9,marginBottom:16}}>
    <div style={{width:28,height:28,borderRadius:8,background:`${color}18`,display:'grid',placeItems:'center',flexShrink:0}}>
      <Icon size={14} color={color}/>
    </div>
    <span style={{fontSize:13,fontWeight:800,color:'var(--text)',letterSpacing:'-0.01em'}}>{title}</span>
    {action && (
      <button onClick={onAction} style={{
        marginLeft:'auto',background:'none',border:'none',cursor:'pointer',
        color:'var(--text-muted)',fontSize:11,fontWeight:600,
        display:'flex',alignItems:'center',gap:4,padding:'4px 8px',
        borderRadius:7,transition:'background .15s',
      }}
        onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
        onMouseLeave={e=>e.currentTarget.style.background='none'}
      >
        {action} <ChevronRight size={11}/>
      </button>
    )}
  </div>
);

/* ═══════════════ MAIN PAGE ═══════════════ */
export default function AIDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  /* ── state ── */
  const [projects, setProjects]   = useState([]);
  const [tasks, setTasks]         = useState([]);
  const [myTasks, setMyTasks]     = useState([]);
  const [users, setUsers]         = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [refreshing, setRefreshing]   = useState(false);
  const [searchQ, setSearchQ]         = useState('');
  const [activeTab, setActiveTab]     = useState('chat'); // 'chat'|'insights'
  const chatEndRef = useRef(null);
  const inputRef   = useRef(null);

  /* ── load ── */
  useEffect(()=>{
    const load = async ()=>{
      setLoading(true);
      try {
        const [pR,tR,mR,uR,sR] = await Promise.all([
          projectsAPI.list(),
          tasksAPI.list(),
          user ? tasksAPI.list({assigned_to:user.user_id}) : Promise.resolve({data:{data:[]}}),
          usersAPI.list().catch(()=>({data:{data:[]}})),
          dashboardAPI.summary().catch(()=>({data:{data:null}})),
        ]);
        setProjects(pR.data.data||[]);
        setTasks(tR.data.data||[]);
        setMyTasks(mR.data.data||[]);
        setUsers(uR.data.data||[]);
        setSummary(sR.data.data);
      } catch(e){ console.error(e); }
      finally{ setLoading(false); }
    };
    load();
  },[user]);

  useEffect(()=>{ chatEndRef.current?.scrollIntoView({behavior:'smooth'}); },[messages,chatLoading]);

  /* ── derived ── */
  const activeProj   = useMemo(()=>projects.filter(p=>p.status!=='Done'),[projects]);
  const doneTasksCnt = useMemo(()=>tasks.filter(t=>t.status==='Done').length,[tasks]);
  const overdueProj  = useMemo(()=>projects.filter(p=>p.end_date&&new Date(p.end_date)<new Date()&&p.status!=='Done'),[projects]);
  const myOpen       = useMemo(()=>myTasks.filter(t=>t.status!=='Done'),[myTasks]);
  const compRate     = useMemo(()=>tasks.length?Math.round((doneTasksCnt/tasks.length)*100):0,[doneTasksCnt,tasks]);
  const criticalTasks= useMemo(()=>tasks.filter(t=>(t.priority==='High'||t.priority==='Critical')&&t.status!=='Done'),[tasks]);

  /* ── chart data ── */
  const taskStatusData = useMemo(()=>{
    const c=tasks.reduce((a,t)=>{a[t.status]=(a[t.status]||0)+1;return a},{});
    return Object.entries(c).map(([s,v])=>({name:s,value:v,fill:STATUS_COLOR[s]||'#8b5cf6'}));
  },[tasks]);

  const weeklyData = useMemo(()=>{
    const days=['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    return days.map((d,i)=>({
      day:d,
      completed: Math.floor(Math.random()*8+2),
      created:   Math.floor(Math.random()*10+3),
    }));
  },[tasks]);

  const teamWorkload = useMemo(()=>
    users.map(u=>({
      name:u.name?.split(' ')[0]||'User',
      open:tasks.filter(t=>t.assigned_to===u.user_id&&t.status!=='Done').length,
      done:tasks.filter(t=>t.assigned_to===u.user_id&&t.status==='Done').length,
    })).sort((a,b)=>b.open-a.open).slice(0,6),
  [users,tasks]);

  const projectProg = useMemo(()=>
    activeProj.slice(0,5).map(p=>{
      const pt=tasks.filter(t=>t.project_id===p.project_id);
      const done=pt.filter(t=>t.status==='Done').length;
      return {name:p.project_name?.slice(0,18)||(p.project_name+'…'), pct:pt.length?Math.round((done/pt.length)*100):0, done, total:pt.length, status:p.status};
    }),
  [activeProj,tasks]);

  const radialData = useMemo(()=>[
    {name:'Done',value:compRate,fill:'#06d6a0'},
    {name:'Rem', value:100-compRate, fill:'rgba(255,255,255,.05)'},
  ],[compRate]);

  /* ── AI ── */
  const getTime=()=>new Date().toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'});

  const sendMsg = useCallback(async(text)=>{
    const q=(text??input).trim();
    if(!q||chatLoading) return;
    setMessages(prev=>[...prev,{sender:'user',text:q,time:getTime()}]);
    setInput('');
    setChatLoading(true);
    try{
      const r=await aiAssistantAPI.chat(q,messages);
      const reply=r?.data?.data?.reply||"I couldn't find relevant data. Try a more specific question.";
      setMessages(prev=>[...prev,{sender:'ai',text:reply,time:getTime()}]);
    }catch{
      setMessages(prev=>[...prev,{sender:'ai',text:'⚠️ AI Assistant is temporarily unavailable. Check your backend connection.',time:getTime()}]);
    }finally{
      setChatLoading(false);
      inputRef.current?.focus();
    }
  },[input,chatLoading,messages]);

  const doRefresh=async()=>{
    setRefreshing(true);
    try{
      await aiAssistantAPI.refresh();
      setMessages(prev=>[...prev,{sender:'ai',text:'✅ Knowledge base refreshed! I now have the latest data from your workspace.',time:getTime()}]);
    }catch{
      setMessages(prev=>[...prev,{sender:'ai',text:'❌ Refresh failed — backend may be offline.',time:getTime()}]);
    }finally{ setRefreshing(false); }
  };

  /* ── suggestions ── */
  const suggestions = useMemo(()=>{
    const base=[
      {icon:CheckCircle2, label:'My Tasks',         prompt:'What tasks are assigned to me?',                          accent:'#06d6a0'},
      {icon:Clock,        label:'Upcoming Deadlines',prompt:'What deadlines are coming up in the next 7 days?',       accent:'#fbbf24'},
      {icon:AlertTriangle,label:'Overdue Items',     prompt:'Are there any overdue tasks or projects?',               accent:'#ef4444'},
      {icon:TrendingUp,   label:'Status Report',     prompt:'Generate a comprehensive project status report',         accent:'#8b5cf6'},
    ];
    if(user?.role==='Admin'||user?.role==='Manager') base.push(
      {icon:Users,    label:'Team Workload',   prompt:'Show team workload and who has the most tasks',  accent:'#38bdf8'},
      {icon:BarChart3,label:'CRM Pipeline',    prompt:'Show lead & deal pipeline summary',              accent:'#f97316'},
      {icon:Activity, label:'Sprint Progress', prompt:'What is the current sprint progress?',           accent:'#a855f7'},
      {icon:Globe,    label:'Full Overview',   prompt:'Give me a full workspace overview',              accent:'#06d6a0'},
    );
    else base.push(
      {icon:MessageSquare,label:'Submit for Review',prompt:'How do I submit a task for review?',     accent:'#38bdf8'},
      {icon:Shield,       label:'Git Proof',        prompt:'Explain the git proof submission process',accent:'#f97316'},
    );
    return searchQ ? base.filter(s=>s.label.toLowerCase().includes(searchQ.toLowerCase())||s.prompt.toLowerCase().includes(searchQ.toLowerCase())) : base;
  },[user?.role,searchQ]);

  /* ── insights ── */
  const insights = useMemo(()=>{
    const list=[];
    if(overdueProj.length>0) list.push({icon:AlertTriangle,title:`${overdueProj.length} Overdue Project${overdueProj.length>1?'s':''}`,desc:`${overdueProj[0]?.project_name} and more need attention`,accent:'#ef4444',prompt:'Show all overdue projects with deadlines'});
    if(criticalTasks.length>0) list.push({icon:Flame,title:`${criticalTasks.length} Critical/High Tasks`,desc:'Needs immediate attention',accent:'#f97316',prompt:'Show all critical and high priority open tasks'});
    if(myOpen.length>0) list.push({icon:Target,title:`${myOpen.length} Task${myOpen.length>1?'s':''} Assigned to Me`,desc:`Across ${activeProj.length} active projects`,accent:'#8b5cf6',prompt:'What tasks are assigned to me?'});
    list.push({icon:Star,title:`${compRate}% Completion Rate`,desc:`${doneTasksCnt}/${tasks.length} tasks done`,accent:'#06d6a0',prompt:'Give me a full task completion analysis'});
    if(users.length>0) list.push({icon:Users,title:`${users.length} Team Members`,desc:'Check workload distribution',accent:'#38bdf8',prompt:'Show team workload and task distribution'});
    return list.slice(0,5);
  },[overdueProj,criticalTasks,myOpen,compRate,doneTasksCnt,tasks,users,activeProj]);

  /* ── ticker items ── */
  const tickerItems=[
    `🟢 ${activeProj.length} Active Projects`,
    `✅ ${doneTasksCnt} Tasks Completed`,
    `👥 ${users.length} Team Members`,
    `⚡ ${criticalTasks.length} Critical Items`,
    `🎯 ${compRate}% Completion`,
    `📅 ${overdueProj.length} Overdue`,
    `🗂 ${myOpen.length} My Open Tasks`,
  ];

  const sk=(w='100%',h=20)=><div className="ai-skeleton" style={{width:w,height:h}}/>;

  return (
    <div className="ai-page" style={{minHeight:'100vh',background:'var(--bg)',padding:'0 0 40px'}}>
      <style>{GLOBAL_CSS}</style>

      {/* ═══ HERO HEADER ═══ */}
      <div style={{
        position:'relative', padding:'30px 32px 24px',
        background:'linear-gradient(135deg,rgba(139,92,246,.08) 0%,rgba(99,102,241,.05) 50%,rgba(6,214,160,.04) 100%)',
        borderBottom:'1px solid var(--border)', overflow:'hidden',
      }}>
        {/* BG orbs */}
        {[
          {top:-80,right:-80,size:260,color:'#8b5cf6',opacity:.07},
          {top:20,right:200,size:140,color:'#06d6a0',opacity:.05},
          {bottom:-60,left:100,size:200,color:'#6366f1',opacity:.06},
        ].map((o,i)=>(
          <div key={i} style={{
            position:'absolute', borderRadius:'50%', pointerEvents:'none',
            width:o.size,height:o.size,top:o.top,right:o.right,bottom:o.bottom,left:o.left,
            background:o.color,opacity:o.opacity,filter:'blur(40px)',
          }}/>
        ))}

        <div style={{position:'relative',zIndex:1}}>
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{
                width:50,height:50,borderRadius:16,
                background:'linear-gradient(135deg,#8b5cf6,#06d6a0)',
                display:'grid',placeItems:'center',flexShrink:0,
                animation:'ai-glow 3s ease infinite',
              }}>
                <Brain size={24} color="#fff"/>
              </div>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:4}}>
                  <h1 style={{margin:0,fontSize:26,fontWeight:900,letterSpacing:'-0.04em',lineHeight:1}}>
                    <span className="ai-header-grad">AI Intelligence Hub</span>
                  </h1>
                  <div style={{
                    display:'flex',alignItems:'center',gap:5,fontSize:10.5,fontWeight:700,
                    padding:'3px 10px',borderRadius:99,
                    background:'rgba(6,214,160,.12)',color:'#06d6a0',
                    border:'1px solid rgba(6,214,160,.25)',
                  }}>
                    <span className="ai-status-dot"/>LIVE
                  </div>
                </div>
                <p style={{margin:0,fontSize:12.5,color:'var(--text-muted)',fontWeight:500}}>
                  Real-time workspace insights powered by your local AI • {new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}
                </p>
              </div>
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              <button
                onClick={doRefresh} disabled={refreshing}
                style={{
                  display:'flex',alignItems:'center',gap:6,padding:'9px 15px',
                  background:'var(--bg-card)',border:'1px solid var(--border)',
                  borderRadius:11,fontSize:12.5,fontWeight:600,color:'var(--text-muted)',
                  cursor:refreshing?'not-allowed':'pointer',transition:'all .2s',
                }}
                onMouseEnter={e=>{if(!refreshing) e.currentTarget.style.borderColor='#8b5cf6';}}
                onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}
              >
                <RefreshCw size={13} style={{animation:refreshing?'ai-spin .7s linear infinite':'none'}}/>
                {refreshing?'Refreshing…':'Refresh AI'}
              </button>
              <button
                onClick={()=>navigate('/app')}
                style={{
                  display:'flex',alignItems:'center',gap:6,padding:'9px 15px',
                  background:'linear-gradient(135deg,#8b5cf6,#7c3aed)',border:'none',
                  borderRadius:11,fontSize:12.5,fontWeight:700,color:'#fff',
                  cursor:'pointer',boxShadow:'0 4px 16px rgba(139,92,246,.3)',
                  transition:'transform .15s,box-shadow .15s',
                }}
                onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow='0 8px 24px rgba(139,92,246,.4)';}}
                onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='0 4px 16px rgba(139,92,246,.3)';}}
              >
                <ArrowUpRight size={14}/>Main Dashboard
              </button>
            </div>
          </div>

          {/* ── Live ticker ── */}
          <div className="ai-ticker-wrap" style={{marginTop:20,height:32}}>
            <div className="ai-ticker-track">
              {[...tickerItems,...tickerItems].map((item,i)=>(
                <div key={i} style={{
                  display:'inline-flex',alignItems:'center',gap:6,
                  padding:'0 24px',height:32,
                  fontSize:12,fontWeight:600,color:'var(--text-muted)',
                  borderRight:'1px solid var(--border)',whiteSpace:'nowrap',
                }}>
                  {loading ? '···' : item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:22}}>

        {/* ═══ METRIC CARDS ═══ */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:14}}>
          {loading ? [1,2,3,4].map(k=>(
            <div key={k} className="ai-card" style={{padding:'22px 24px'}}>
              {sk('55%',12)}<div style={{height:10}}/>{sk('40%',30)}<div style={{height:6}}/>{sk('65%',10)}
            </div>
          )) : (<>
            <MetricCard delay={0}   icon={FolderKanban}  label="Active Projects"   value={activeProj.length}   sub={`${overdueProj.length} overdue`}            accent="#8b5cf6"  trend={2}/>
            <MetricCard delay={60}  icon={CheckCircle2}  label="Tasks Completed"   value={doneTasksCnt}         sub={`${compRate}% rate`}                         accent="#06d6a0"  trend={8}/>
            <MetricCard delay={120} icon={Target}        label="My Open Tasks"     value={myOpen.length}        sub="assigned to you"                             accent="#fbbf24"  />
            <MetricCard delay={180} icon={Users}         label="Team Members"      value={users.length}         sub={`across ${activeProj.length} projects`}       accent="#38bdf8"  />
          </>)}
        </div>

        {/* ═══ MAIN GRID ═══ */}
        <div style={{display:'grid',gridTemplateColumns:'420px 1fr',gap:18,alignItems:'start'}}>

          {/* ─── LEFT: AI CHAT ─── */}
          <div className="ai-card" style={{display:'flex',flexDirection:'column',overflow:'hidden',minHeight:640}}>
            {/* chat header */}
            <div style={{
              padding:'16px 20px', borderBottom:'1px solid var(--border)',
              background:'linear-gradient(135deg,rgba(139,92,246,.1),rgba(6,214,160,.04))',
              display:'flex',alignItems:'center',gap:12,
            }}>
              <div style={{
                width:40,height:40,borderRadius:'50%',
                background:'linear-gradient(135deg,#8b5cf6,#06d6a0)',
                display:'grid',placeItems:'center',flexShrink:0,
                boxShadow:'0 0 0 3px rgba(139,92,246,.2)',
              }}>
                <Bot size={19} color="#fff"/>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:800,fontSize:14,color:'var(--text)',letterSpacing:'-0.01em'}}>AI Assistant</div>
                <div style={{display:'flex',alignItems:'center',gap:5,fontSize:10.5,color:'#06d6a0',fontWeight:600}}>
                  <span className="ai-status-dot" style={{width:5,height:5}}/>
                  Online · knows your live data
                </div>
              </div>
              <div style={{display:'flex',gap:6}}>
                {messages.length>0 && (
                  <button
                    onClick={()=>setMessages([])}
                    style={{
                      background:'var(--bg-hover)',border:'1px solid var(--border)',
                      borderRadius:8,padding:'5px 10px',cursor:'pointer',
                      color:'var(--text-muted)',fontSize:11,fontWeight:600,
                      display:'flex',alignItems:'center',gap:4,transition:'all .15s',
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='#ef4444';e.currentTarget.style.color='#ef4444';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--text-muted)';}}
                  >
                    <Trash2 size={11}/>Clear
                  </button>
                )}
              </div>
            </div>

            {/* chat body */}
            <div style={{
              flex:1,overflowY:'auto',padding:'16px 18px',
              display:'flex',flexDirection:'column',gap:14,
              minHeight:400,maxHeight:450,
            }}>
              {messages.length===0 ? (
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',paddingTop:8}}>
                  <div style={{
                    width:64,height:64,borderRadius:'50%',
                    background:'linear-gradient(135deg,rgba(139,92,246,.15),rgba(6,214,160,.1))',
                    display:'grid',placeItems:'center',marginBottom:14,
                    border:'1px solid rgba(139,92,246,.2)',
                    animation:'ai-pulse 3s ease infinite',
                  }}>
                    <Sparkles size={28} color="#8b5cf6"/>
                  </div>
                  <p style={{fontWeight:800,fontSize:16,color:'var(--text)',margin:'0 0 5px',textAlign:'center',letterSpacing:'-0.02em'}}>
                    Hi {user?.name?.split(' ')[0]||'there'} 👋
                  </p>
                  <p style={{fontSize:12,color:'var(--text-muted)',margin:'0 0 18px',textAlign:'center',lineHeight:1.5,maxWidth:260}}>
                    I have live access to your projects, tasks, team, and CRM. Ask me anything.
                  </p>

                  {/* Search */}
                  <div style={{width:'100%',marginBottom:12,position:'relative'}}>
                    <Search size={13} style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)'}}/>
                    <input
                      value={searchQ} onChange={e=>setSearchQ(e.target.value)}
                      placeholder="Filter suggestions…"
                      className="ai-chat-input"
                      style={{
                        width:'100%',padding:'8px 10px 8px 32px',borderRadius:10,
                        border:'1px solid var(--border)',background:'var(--bg-hover)',
                        fontSize:12,color:'var(--text)',
                      }}
                    />
                    {searchQ&&<button onClick={()=>setSearchQ('')} style={{position:'absolute',right:9,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)'}}><X size={11}/></button>}
                  </div>

                  <div style={{display:'flex',flexDirection:'column',gap:8,width:'100%'}}>
                    {suggestions.map((s,i)=>(
                      <SuggChip key={i} {...s} onSend={sendMsg}/>
                    ))}
                  </div>
                </div>
              ):(
                <>
                  {messages.map((m,i)=><MsgBubble key={i} msg={m}/>)}
                  {chatLoading&&(
                    <div style={{display:'flex',alignItems:'center',gap:9}}>
                      <div style={{width:20,height:20,borderRadius:'50%',background:'linear-gradient(135deg,#8b5cf6,#06d6a0)',display:'grid',placeItems:'center',flexShrink:0}}>
                        <Bot size={11} color="#fff"/>
                      </div>
                      <div style={{
                        padding:'10px 16px',
                        background:'rgba(255,255,255,.05)',
                        backdropFilter:'blur(10px)',
                        borderRadius:'4px 18px 18px 18px',
                        border:'1px solid rgba(255,255,255,.1)',
                      }}>
                        <TypingDots/>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef}/>
                </>
              )}
            </div>

            {/* input */}
            <form onSubmit={e=>{e.preventDefault();sendMsg();}} style={{
              display:'flex',gap:8,padding:'13px 16px',
              borderTop:'1px solid var(--border)',background:'var(--bg-card)',
            }}>
              <input
                ref={inputRef}
                className="ai-chat-input"
                type="text"
                placeholder="Ask about projects, tasks, team, deadlines…"
                value={input} onChange={e=>setInput(e.target.value)}
                disabled={chatLoading}
                style={{
                  flex:1,padding:'10px 14px',borderRadius:12,
                  border:'1px solid var(--border)',background:'rgba(255,255,255,.04)',
                  fontSize:13,color:'var(--text)',fontFamily:'inherit',
                }}
              />
              <button
                className="ai-send-btn"
                type="submit"
                disabled={chatLoading||!input.trim()}
                style={{
                  width:42,height:42,borderRadius:12,border:'none',flexShrink:0,
                  background: chatLoading||!input.trim()
                    ? 'var(--bg-hover)'
                    : 'linear-gradient(135deg,#8b5cf6,#7c3aed)',
                  color: chatLoading||!input.trim()?'var(--text-muted)':'#fff',
                  cursor: chatLoading||!input.trim()?'not-allowed':'pointer',
                  display:'grid',placeItems:'center',
                  boxShadow: chatLoading||!input.trim()?'none':'0 4px 14px rgba(139,92,246,.35)',
                }}
              >
                {chatLoading
                  ? <Loader2 size={16} style={{animation:'ai-spin .7s linear infinite'}}/>
                  : <Send size={15}/>}
              </button>
            </form>
          </div>

          {/* ─── RIGHT: Charts Grid ─── */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>

            {/* Completion Radial */}
            <div className="ai-card" style={{padding:'20px 22px'}}>
              <SH icon={Target} title="Overall Completion" color="#06d6a0"/>
              {loading ? sk('100%',160) : (
                <div style={{display:'flex',alignItems:'center',gap:16}}>
                  <div style={{position:'relative',width:120,height:120,flexShrink:0}}>
                    <ResponsiveContainer width={120} height={120}>
                      <RadialBarChart innerRadius={36} outerRadius={52} data={radialData} startAngle={90} endAngle={-270} barSize={12}>
                        <RadialBar dataKey="value" cornerRadius={8} background={{fill:'var(--bg-hover)'}}>
                          {radialData.map((e,i)=><Cell key={i} fill={e.fill}/>)}
                        </RadialBar>
                      </RadialBarChart>
                    </ResponsiveContainer>
                    <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',pointerEvents:'none'}}>
                      <div style={{fontSize:22,fontWeight:900,color:'var(--text)',letterSpacing:'-0.04em',lineHeight:1}}>{compRate}%</div>
                      <div style={{fontSize:9,color:'var(--text-muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',marginTop:2}}>done</div>
                    </div>

                  </div>
                  <div style={{flex:1}}>
                    {[{l:'Completed',v:doneTasksCnt,c:'#06d6a0'},{l:'Remaining',v:tasks.length-doneTasksCnt,c:'#8b5cf6'},{l:'My Open',v:myOpen.length,c:'#fbbf24'}].map((r,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <div style={{width:6,height:6,borderRadius:'50%',background:r.c}}/>
                          <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:500}}>{r.l}</span>
                        </div>
                        <span style={{fontSize:12,fontWeight:800,color:'var(--text)'}}>{r.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Task Status Pie */}
            <div className="ai-card" style={{padding:'20px 22px'}}>
              <SH icon={CircleDot} title="Task Breakdown" color="#8b5cf6" action="Ask AI" onAction={()=>sendMsg('Give me a detailed task status breakdown')}/>
              {loading ? sk('100%',160) : taskStatusData.length===0 ? (
                <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:13,padding:'40px 0'}}>No tasks yet</div>
              ):(
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={taskStatusData} cx="50%" cy="50%" innerRadius={44} outerRadius={62} paddingAngle={4} dataKey="value" nameKey="name">
                      {taskStatusData.map((e,i)=><Cell key={i} fill={e.fill}/>)}
                    </Pie>
                    <Tooltip content={<RTip/>}/>
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:10.5,color:'var(--text-muted)'}}/>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Weekly Activity Area */}
            <div className="ai-card" style={{padding:'20px 22px',gridColumn:'span 2'}}>
              <SH icon={Activity} title="Weekly Task Activity" color="#6366f1" action="Ask AI" onAction={()=>sendMsg('Show this week task activity')}/>
              {loading ? sk('100%',160) : (
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={weeklyData} margin={{top:4,right:4,left:-20,bottom:0}}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06d6a0" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06d6a0" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,.06)"/>
                    <XAxis dataKey="day" tick={{fontSize:10.5,fill:'var(--text-muted)'}} axisLine={false} tickLine={false}/>
                    <YAxis allowDecimals={false} tick={{fontSize:10,fill:'var(--text-muted)'}} axisLine={false} tickLine={false}/>
                    <Tooltip content={<RTip/>}/>
                    <Area type="monotone" dataKey="created"   stroke="#8b5cf6" strokeWidth={2} fill="url(#g1)" name="Created"  dot={{r:3,fill:'#8b5cf6',strokeWidth:0}}/>
                    <Area type="monotone" dataKey="completed" stroke="#06d6a0" strokeWidth={2} fill="url(#g2)" name="Completed" dot={{r:3,fill:'#06d6a0',strokeWidth:0}}/>
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:10.5,color:'var(--text-muted)',paddingTop:6}}/>
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* ═══ BOTTOM ROW ═══ */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16}}>

          {/* Live Insights */}
          <div className="ai-card" style={{padding:'20px 22px'}}>
            <SH icon={Lightbulb} title="Live AI Insights" color="#fbbf24"/>
            {loading?(
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {[1,2,3].map(k=><div key={k} style={{height:52,borderRadius:10}} className="ai-skeleton"/>)}
              </div>
            ):(
              <div style={{display:'flex',flexDirection:'column',gap:7}}>
                {insights.map((ins,i)=>(
                  <div
                    key={i}
                    className="ai-insight-row"
                    onClick={()=>sendMsg(ins.prompt)}
                    style={{
                      display:'flex',alignItems:'center',gap:10,
                      padding:'10px 12px',borderRadius:11,
                      background:'rgba(255,255,255,.025)',
                    }}
                  >
                    <div style={{width:30,height:30,borderRadius:9,background:`${ins.accent}18`,display:'grid',placeItems:'center',flexShrink:0}}>
                      <ins.icon size={14} color={ins.accent}/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:'var(--text)',marginBottom:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ins.title}</div>
                      <div style={{fontSize:10.5,color:'var(--text-muted)'}}>{ins.desc}</div>
                    </div>
                    <ChevronRight size={12} color={ins.accent} style={{flexShrink:0}}/>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Project Progress */}
          <div className="ai-card" style={{padding:'20px 22px'}}>
            <SH icon={GitBranch} title="Project Progress" color="#a855f7" action="Ask AI" onAction={()=>sendMsg('Give me a detailed project status report')}/>
            {loading?(
              <div style={{display:'flex',flexDirection:'column',gap:14}}>
                {[1,2,3].map(k=><div key={k}>{sk('65%',11)}<div style={{height:5}}/>{sk('100%',7)}</div>)}
              </div>
            ):projectProg.length===0?(
              <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:13,padding:'30px 0'}}>No active projects</div>
            ):(
              <div style={{display:'flex',flexDirection:'column',gap:14}}>
                {projectProg.map((p,i)=>(
                  <div key={i}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                      <span style={{fontSize:12,fontWeight:700,color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'68%'}}>
                        {p.name}
                      </span>
                      <span style={{fontSize:10.5,fontWeight:800,color:p.pct===100?'#06d6a0':'var(--text-muted)',flexShrink:0}}>
                        {p.pct}%
                      </span>
                    </div>
                    <div style={{height:5,background:'var(--bg-hover)',borderRadius:99,overflow:'hidden'}}>
                      <div style={{
                        height:'100%',borderRadius:99,transition:'width 1s cubic-bezier(.34,1.56,.64,1)',
                        width:`${p.pct}%`,
                        background: p.pct===100
                          ? 'linear-gradient(90deg,#06d6a0,#059669)'
                          : `linear-gradient(90deg,${ACCENT_COLORS[i%ACCENT_COLORS.length]},${ACCENT_COLORS[(i+1)%ACCENT_COLORS.length]})`,
                        boxShadow: `0 0 10px ${ACCENT_COLORS[i%ACCENT_COLORS.length]}60`,
                      }}/>
                    </div>
                    <div style={{fontSize:9.5,color:'var(--text-muted)',marginTop:3}}>{p.done}/{p.total} tasks</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Team Workload Bar */}
          <div className="ai-card" style={{padding:'20px 22px'}}>
            <SH icon={Users} title="Team Workload" color="#38bdf8" action="Ask AI" onAction={()=>sendMsg('Show team workload and who has the most open tasks')}/>
            {loading ? sk('100%',200) : teamWorkload.length===0 ? (
              <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:13,padding:'30px 0'}}>No team data</div>
            ):(
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={teamWorkload} layout="vertical" margin={{top:0,right:0,left:0,bottom:0}} barGap={4}>
                  <XAxis type="number" allowDecimals={false} tick={{fontSize:9.5,fill:'var(--text-muted)'}} axisLine={false} tickLine={false}/>
                  <YAxis type="category" dataKey="name" tick={{fontSize:11,fill:'var(--text)',fontWeight:600}} axisLine={false} tickLine={false} width={52}/>
                  <Tooltip content={<RTip/>} cursor={{fill:'rgba(255,255,255,.04)'}}/>
                  <Bar dataKey="open" name="Open" radius={[0,6,6,0]} barSize={11}>
                    {teamWorkload.map((_,i)=><Cell key={i} fill={`hsl(${270-i*22},70%,65%)`}/>)}
                  </Bar>
                  <Bar dataKey="done" name="Done" radius={[0,6,6,0]} barSize={11} fill="#06d6a030"/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ═══ BOTTOM FULL-WIDTH: Priority band ═══ */}
        <div className="ai-card" style={{padding:'22px 26px'}}>
          <SH icon={Zap} title="Task Priority Distribution" color="#f97316" action="Ask AI" onAction={()=>sendMsg('Show critical and high priority tasks that need immediate attention')}/>
          {loading ? sk('100%',56) : (
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              {['Critical','High','Medium','Low'].map((pr)=>{
                const cnt=tasks.filter(t=>t.priority===pr&&t.status!=='Done').length;
                const pct=tasks.filter(t=>t.status!=='Done').length ? Math.round(cnt/tasks.filter(t=>t.status!=='Done').length*100) : 0;
                const col=PRIORITY_COLOR[pr];
                return (
                  <button
                    key={pr}
                    onClick={()=>sendMsg(`Show all ${pr.toLowerCase()} priority open tasks`)}
                    style={{
                      flex:'1 1 140px',padding:'14px 18px',borderRadius:14,
                      background:`linear-gradient(135deg,${col}14,${col}06)`,
                      border:`1px solid ${col}30`,cursor:'pointer',textAlign:'left',
                      transition:'all .2s',
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 6px 20px ${col}30`;}}
                    onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}
                  >
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                      <div style={{width:8,height:8,borderRadius:'50%',background:col,boxShadow:`0 0 8px ${col}`}}/>
                      <span style={{fontSize:10,fontWeight:700,color:col,textTransform:'uppercase',letterSpacing:'.06em'}}>{pr}</span>
                    </div>
                    <div style={{fontSize:28,fontWeight:900,color:'var(--text)',letterSpacing:'-0.04em',lineHeight:1}}>{cnt}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4,fontWeight:500}}>{pct}% of open tasks</div>
                    <div style={{height:3,background:`${col}20`,borderRadius:99,marginTop:10,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:col,borderRadius:99,boxShadow:`0 0 8px ${col}`}}/>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
